<template>
	<v-container fluid class="px-0 entregas-graphs">
		<v-row v-if="loading">
			<v-col>
			<div class="d-flex flex-column align-center justify-center loading-container rounded-lg pa-3 text-center py-8">
				<v-progress-circular
				:size="70"
				:width="4"
				color="#5e85ef"
				indeterminate
				></v-progress-circular>
				<p class="mb-0 mt-5 text--disabled">Cargando Información</p>
			</div>
			</v-col>
		</v-row>
		<div v-else>
			<!-- Header -->
			<div class="dashboard-header mb-6">
				<div class="header-text">
					<span class="header-eyebrow">Panel de entregas</span>
					<h2 class="header-title">Resumen de Entregas</h2>
					<p class="header-subtitle">Visualiza el estado y avance general de las entregas del proyecto</p>
				</div>
				<v-btn
					color="#5E85EF"
					dark
					depressed
					small
					class="export-btn"
					:loading="exportingPdf"
					:disabled="exportingPdf || !$store.projectsStore.state.selectedProject"
					@click="exportGraphsPDF"
				>
					<v-icon left size="16">mdi-file-pdf-box</v-icon>
					Exportar PDF
				</v-btn>
			</div>

			<!-- Summary Statistics Cards -->
			<div class="stats-grid mb-6">
				<div
					v-for="card in statCardsConfig"
					:key="card.key"
					class="stat-card"
					:style="{ '--accent': card.color }"
				>
					<div class="stat-icon-wrap" :style="{ background: card.bg }">
						<v-icon :color="card.color" size="22">{{ card.icon }}</v-icon>
					</div>
					<div class="stat-body">
						<h3 class="stat-value">{{ card.value }}</h3>
						<p class="stat-label">{{ card.label }}</p>
					</div>
				</div>
			</div>

			<v-row>
				<!-- First Row: Status Distribution and Time Analysis -->
				<v-col cols="12" md="6">
				<div class="chart-card" :style="{ '--chart-accent': '#5E85EF' }">
					<div class="chart-card-header">
						<div class="chart-header-icon" :style="{ background: '#5E85EF1A' }">
							<v-icon color="#5E85EF" size="20">mdi-chart-pie</v-icon>
						</div>
						<div class="chart-header-text">
							<h3 class="chart-title">Entregas por Estado</h3>
							<p class="chart-subtitle">Distribución de entregas según su estado actual</p>
						</div>
					</div>
					<div class="chart-body">
					<DoughnutChart
						v-if="chartData.statusDistribution.labels.length > 0"
						:chartdata="{
							labels: statusDistributionChartLabels,
							datasets: [
								{
									backgroundColor: chartData.statusDistribution.colors,
									data: chartData.statusDistribution.data
								}
							]
						}"
						:options="{
							responsive: true,
							maintainAspectRatio: false,
							plugins: {
								datalabels: {
									anchor: 'center',
									align: 'center',
									color: getDataLabelColor,
									display: displayDataLabel,
									font: {
										weight: 'bold',
										size: 12
									},
									formatter: formatDataLabel
								}
							},
							legend: {
							position: 'right',
							labels: {
								padding: 20,
								boxWidth: 10
							}
							}
						}"
						:height="250"
						:showDataLabels="true"
					/>
					<div v-else class="empty-chart-message">
					<v-icon color="#C7CDD9" size="48">mdi-chart-pie</v-icon>
					<p>No hay datos disponibles</p>
					</div>
					</div>
				</div>
				</v-col>

				<v-col cols="12" md="6">
				<div class="chart-card" :style="{ '--chart-accent': '#EAB261' }">
					<div class="chart-card-header">
						<div class="chart-header-icon" :style="{ background: '#EAB2611A' }">
							<v-icon color="#EAB261" size="20">mdi-chart-timeline-variant</v-icon>
						</div>
						<div class="chart-header-text">
							<h3 class="chart-title">Tiempo Promedio de Aprobación</h3>
							<p class="chart-subtitle">Días promedio hasta la aprobación por especialidad</p>
						</div>
					</div>
					<div class="chart-body">
					<BarChart
						v-if="chartData.approvalTime.labels.length > 0"
						:chartdata="{
							labels: chartData.approvalTime.labels,
							datasets: [
							{
								backgroundColor: chartData.approvalTime.colors,
								label: 'Días',
								data: chartData.approvalTime.data
							}
							]
						}"
						:options="{
							responsive: true,
							maintainAspectRatio: false,
							plugins: {
								datalabels: {
									anchor: 'center',
									align: 'center',
									color: getDataLabelColor,
									display: displayDataLabel,
									font: {
										weight: 'bold',
										size: 11
									},
									formatter: formatDataLabel
								}
							},
							legend: {
							display: false
							},
							scales: {
							yAxes: [{
								ticks: {
								beginAtZero: true
								}
							}]
							}
						}"
						:height="250"
						:showDataLabels="true"
					/>
					<div v-else class="empty-chart-message">
					<v-icon color="#C7CDD9" size="48">mdi-chart-timeline</v-icon>
					<p>No hay datos disponibles</p>
					</div>
					</div>
				</div>
				</v-col>

				<!-- Second Row: Approvals by Job Type and Monthly Trends -->
				<v-col cols="12" md="6">
				<div class="chart-card" :style="{ '--chart-accent': '#9C27B0' }">
					<div class="chart-card-header">
						<div class="chart-header-icon" :style="{ background: '#9C27B01A' }">
							<v-icon color="#9C27B0" size="20">mdi-account-group</v-icon>
						</div>
						<div class="chart-header-text">
							<h3 class="chart-title">Aprobaciones por Perfil</h3>
							<p class="chart-subtitle">Entregas únicas por estado según perfil del aprobador</p>
						</div>
					</div>
					<div class="chart-body">
					<HorizontalBarChart
						v-if="chartData.approvalsByJobType.labels.length > 0"
						:chartdata="{
							labels: chartData.approvalsByJobType.labels,
							datasets: [
							{
								backgroundColor: '#EAB261',
								label: 'Aprobadas',
								data: chartData.approvalsByJobType.approved
							},
							{
								backgroundColor: '#F45771',
								label: 'Rechazadas',
								data: chartData.approvalsByJobType.rejected
							},
							{
								backgroundColor: '#9E9E9E',
								label: 'Observadas',
								data: chartData.approvalsByJobType.observed
							}
							]
						}"
						:options="{
							responsive: true,
							maintainAspectRatio: false,
							plugins: {
								datalabels: {
									anchor: 'center',
									align: 'center',
									color: getDataLabelColor,
									clamp: true,
									display: displayDataLabel,
									font: {
										weight: 'bold',
										size: 11
									},
									formatter: formatDataLabel
								}
							},
							legend: {
							position: 'top',
							labels: {
								boxWidth: 10
							}
							},
							scales: {
							xAxes: [{
								stacked: true,
								ticks: {
								beginAtZero: true
								}
							}],
							yAxes: [{
								stacked: true
							}]
							}
						}"
						:height="250"
						:showDataLabels="true"
					/>
					<div v-else class="empty-chart-message">
					<v-icon color="#C7CDD9" size="48">mdi-account-group</v-icon>
					<p>No hay datos disponibles</p>
					</div>
					</div>
				</div>
				</v-col>

				<v-col cols="12" md="6">
				<div class="chart-card" :style="{ '--chart-accent': '#2D4B95' }">
					<div class="chart-card-header">
						<div class="chart-header-icon" :style="{ background: '#2D4B951A' }">
							<v-icon color="#2D4B95" size="20">mdi-account</v-icon>
						</div>
						<div class="chart-header-text">
							<h3 class="chart-title">Aprobaciones por Usuario</h3>
							<p class="chart-subtitle">Entregas únicas por estado según usuario del proyecto</p>
						</div>
					</div>
					<div class="chart-body">
					<HorizontalBarChart
						v-if="chartData.approvalsByUser.labels.length > 0"
						:chartdata="{
							labels: chartData.approvalsByUser.labels,
							datasets: [
							{
								backgroundColor: '#EAB261',
								label: 'Aprobadas',
								data: chartData.approvalsByUser.approved
							},
							{
								backgroundColor: '#F45771',
								label: 'Rechazadas',
								data: chartData.approvalsByUser.rejected
							},
							{
								backgroundColor: '#9E9E9E',
								label: 'Observadas',
								data: chartData.approvalsByUser.observed
							}
							]
						}"
						:options="{
							responsive: true,
							maintainAspectRatio: false,
							plugins: {
								datalabels: {
									anchor: 'center',
									align: 'center',
									color: getDataLabelColor,
									clamp: true,
									display: displayDataLabel,
									font: {
										weight: 'bold',
										size: 11
									},
									formatter: formatDataLabel
								}
							},
							legend: {
							position: 'top',
							labels: {
								boxWidth: 10
							}
							},
							scales: {
							xAxes: [{
								stacked: true,
								ticks: {
								beginAtZero: true
								}
							}],
							yAxes: [{
								stacked: true
							}]
							}
						}"
						:height="250"
						:showDataLabels="true"
					/>
					<div v-else class="empty-chart-message">
					<v-icon color="#C7CDD9" size="48">mdi-account</v-icon>
					<p>No hay datos disponibles</p>
					</div>
					</div>
				</div>
				</v-col>

				<v-col cols="12" md="6">
				<div class="chart-card" :style="{ '--chart-accent': '#5E85EF' }">
					<div class="chart-card-header">
						<div class="chart-header-icon" :style="{ background: '#5E85EF1A' }">
							<v-icon color="#5E85EF" size="20">mdi-chart-line</v-icon>
						</div>
						<div class="chart-header-text">
							<h3 class="chart-title">Tendencia Mensual de Entregas</h3>
							<p class="chart-subtitle">Número de entregas creadas por mes</p>
						</div>
					</div>
					<div class="chart-body">
					<LineChart
						v-if="chartData.monthlyTrend.labels.length > 0"
						:chartdata="{
							labels: chartData.monthlyTrend.labels,
							datasets: [
							{
								label: 'Entregas',
								borderColor: '#5E85EF',
								backgroundColor: 'rgba(94, 133, 239, 0.2)',
								data: chartData.monthlyTrend.data,
								fill: true
							}
							]
						}"
						:options="{
							responsive: true,
							maintainAspectRatio: false,
							legend: {
							display: false
							},
							elements: {
							line: {
								tension: 0.4
							},
							point: {
								radius: 4
							}
							},
							scales: {
							yAxes: [{
								ticks: {
								beginAtZero: true,
								precision: 0
								}
							}]
							}
						}"
						:height="250"
					/>
					<div v-else class="empty-chart-message">
					<v-icon color="#C7CDD9" size="48">mdi-chart-line</v-icon>
					<p>No hay datos disponibles</p>
					</div>
					</div>
				</div>
				</v-col>

				<!-- Third Row: Zone Distribution and Deliveries by Specialty -->
				<v-col cols="12" md="6">
				<div class="chart-card" :style="{ '--chart-accent': '#009688' }">
					<div class="chart-card-header">
						<div class="chart-header-icon" :style="{ background: '#0096881A' }">
							<v-icon color="#009688" size="20">mdi-map-marker-radius</v-icon>
						</div>
						<div class="chart-header-text">
							<h3 class="chart-title">Distribución por Zona</h3>
							<p class="chart-subtitle">Cantidad de entregas por zona del proyecto</p>
						</div>
					</div>
					<div class="chart-body">
					<PolarAreaChart
						v-if="chartData.zoneDistribution.labels.length > 0"
						:chartdata="{
							labels: chartData.zoneDistribution.labels,
							datasets: [
							{
								backgroundColor: chartData.zoneDistribution.colors,
								data: chartData.zoneDistribution.data
							}
							]
						}"
						:options="{
							responsive: true,
							maintainAspectRatio: false,
							plugins: {
								datalabels: {
									anchor: 'center',
									align: 'center',
									color: getDataLabelColor,
									clamp: true,
									display: displayDataLabel,
									font: {
										weight: 'bold',
										size: 12
									},
									formatter: formatDataLabel
								}
							},
							legend: {
							position: 'right',
							labels: {
								padding: 20,
								boxWidth: 10
							}
							},
							scale: {
							ticks: {
								beginAtZero: true
							}
							}
						}"
						:height="250"
						:showDataLabels="true"
					/>
					<div v-else class="empty-chart-message">
					<v-icon color="#C7CDD9" size="48">mdi-map-marker-radius</v-icon>
					<p>No hay datos disponibles</p>
					</div>
					</div>
				</div>
				</v-col>

				<v-col cols="12" md="6">
				<div class="chart-card" :style="{ '--chart-accent': '#8BC34A' }">
					<div class="chart-card-header">
						<div class="chart-header-icon" :style="{ background: '#8BC34A1A' }">
							<v-icon color="#8BC34A" size="20">mdi-format-list-bulleted-type</v-icon>
						</div>
						<div class="chart-header-text">
							<h3 class="chart-title">Entregas por Especialidad</h3>
							<p class="chart-subtitle">Entregas únicas por estado dentro de cada especialidad</p>
						</div>
					</div>
					<div class="chart-body">
					<HorizontalBarChart
						v-if="chartData.deliveriesBySpecialty.labels.length > 0"
						:chartdata="{
																												labels: chartData.deliveriesBySpecialty.labels,
							datasets: [
							{
								backgroundColor: '#EAB261',
								label: 'Aprobadas',
								data: chartData.deliveriesBySpecialty.approved
							},
							{
								backgroundColor: '#F45771',
								label: 'Rechazadas',
								data: chartData.deliveriesBySpecialty.rejected
							},
							{
								backgroundColor: '#9E9E9E',
								label: 'Observadas',
								data: chartData.deliveriesBySpecialty.observed
							},
							{
								backgroundColor: '#90A4AE',
								label: 'Pendientes',
								data: chartData.deliveriesBySpecialty.pending
							}
							]
						}"
						:options="{
							responsive: true,
							maintainAspectRatio: false,
							plugins: {
								datalabels: {
									anchor: 'center',
									align: 'center',
									color: getDataLabelColor,
									clamp: true,
									display: displayDataLabel,
									font: {
										weight: 'bold',
										size: 11
									},
									formatter: formatDataLabel
								}
							},
							legend: {
								position: 'top',
								labels: {
									boxWidth: 10
								}
							},
							scales: {
							xAxes: [{
								stacked: true,
								ticks: {
									beginAtZero: true
								}
							}],
							yAxes: [{
								stacked: true
							}]
							}
						}"
						:height="250"
						:showDataLabels="true"
					/>
					<div v-else class="empty-chart-message">
					<v-icon color="#C7CDD9" size="48">mdi-format-list-bulleted-type</v-icon>
					<p>No hay datos disponibles</p>
					</div>
					</div>
				</div>
				</v-col>
			</v-row>
		</div>
	</v-container>
</template>

<script>
import BarChart from '@/components/Charts/Bar'
import HorizontalBarChart from '@/components/Charts/HorizontalBar'
import DoughnutChart from '@/components/Charts/Doughnut'
import LineChart from '@/components/Charts/Line'
import PolarAreaChart from '@/components/Charts/PolarArea'
import Chart from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'

let dataLabelsPluginRegistered = false

if (!dataLabelsPluginRegistered) {
	Chart.plugins.register(ChartDataLabels)
	dataLabelsPluginRegistered = true
}

export default {
	name: 'EntregasGraphs',
	components: {
	BarChart,
	HorizontalBarChart,
	DoughnutChart,
	LineChart,
	PolarAreaChart
	},
	data: () => ({
		loading: true,
		exportingPdf: false,
		// Segundos entre cada recarga automática de los datos mientras el componente esté activo
		refreshIntervalSeconds: 60,
		autoRefreshTimer: null,
		chartColors: [
			'#2D4B95',
			'#5E85EF',
			'#009688',
			'#EAB261',
			'#8BC34A',
			'#FF9800',
			'#9C27B0',
			'#F45771'
		],
		chartData: {
			statusDistribution: {
				labels: [],
				data: [],
				colors: []
			},
			approvalTime: {
				labels: [],
				data: [],
				colors: []
			},
			approvalsByJobType: {
				labels: [],
				approved: [],
				rejected: [],
				observed: []
			},
			approvalsByUser: {
				labels: [],
				approved: [],
				rejected: [],
				observed: []
			},
			monthlyTrend: {
				labels: [],
				data: []
			},
			zoneDistribution: {
				labels: [],
				data: [],
				colors: []
			},
			deliveriesBySpecialty: {
				labels: [],
				approved: [],
				rejected: [],
				observed: [],
				pending: []
			}
		},
		statCards: {
			totalEntregas: 0,
			pendingEntregas: 0,
			approvedEntregas: 0,
			overdueEntregas: 0,
			revItoEntregas: 0,
			aprobadoAcEntregas: 0,
			aprobadoAcTlEntregas: 0
		}
	}),

	computed: {
		statusDistributionChartLabels() {
			return this.chartData.statusDistribution.labels.map((label, index) => {
				const value = this.chartData.statusDistribution.data[index] || 0;
				return `${label} (${value})`;
			});
		},

		statCardsConfig() {
			const cards = [
				{ key: 'total', icon: 'mdi-file-document-multiple', color: '#5E85EF', label: 'Total', value: this.statCards.totalEntregas },
				{ key: 'pending', icon: 'mdi-clock-outline', color: '#FFC107', label: 'Pendientes', value: this.statCards.pendingEntregas },
				{ key: 'approved', icon: 'mdi-check-circle', color: '#4CAF50', label: 'Aprobadas', value: this.statCards.approvedEntregas },
				{ key: 'overdue', icon: 'mdi-alert-circle', color: '#F44336', label: 'Atrasadas', value: this.statCards.overdueEntregas },
				{ key: 'revIto', icon: 'mdi-clipboard-search-outline', color: '#FF9800', label: 'En Rev. ITO', value: this.statCards.revItoEntregas },
				{ key: 'aprobadoAc', icon: 'mdi-check-decagram', color: '#8BC34A', label: 'Aprobado A/C', value: this.statCards.aprobadoAcEntregas },
				{ key: 'aprobadoAcTl', icon: 'mdi-check-all', color: '#009688', label: 'Aprobado A/C TL', value: this.statCards.aprobadoAcTlEntregas }
			]

			return cards.map(card => ({ ...card, bg: `${card.color}1A` }))
		}
	},
	
	mounted() {
		if (this.$store.projectsStore.state.selectedProject) {
			this.fetchEntregaGraphData();
		}
		this.startAutoRefresh();
	},

	beforeDestroy() {
		this.stopAutoRefresh();
	},
	
	watch: {
		'$store.projectsStore.state.selectedProject': function(newVal) {
			if (newVal) {
			this.fetchEntregaGraphData();
			}
		},
		'$store.entregaStore.state.entregaGraphsData': function(newVal) {
			if (newVal && newVal.status === 1 && newVal.data) {
			this.loading = false;
			this.processGraphData(newVal.data);
			}
		}
	},
	
	methods: {
		displayDataLabel(context) {
			const value = context.dataset && context.dataset.data
				? context.dataset.data[context.dataIndex]
				: 0

			return Number(value) > 0
		},

		formatDataLabel(value) {
			return Number(value).toLocaleString('es-CL')
		},

		getDataLabelColor(context) {
			const backgroundColors = context.dataset && context.dataset.backgroundColor
			const backgroundColor = Array.isArray(backgroundColors)
				? backgroundColors[context.dataIndex]
				: backgroundColors

			if (typeof backgroundColor !== 'string') {
				return '#FFFFFF'
			}

			let red
			let green
			let blue
			const hex = backgroundColor.replace('#', '')

			if (/^[0-9a-f]{3}$/i.test(hex)) {
				red = parseInt(hex.charAt(0) + hex.charAt(0), 16)
				green = parseInt(hex.charAt(1) + hex.charAt(1), 16)
				blue = parseInt(hex.charAt(2) + hex.charAt(2), 16)
			} else if (/^[0-9a-f]{6,8}$/i.test(hex)) {
				red = parseInt(hex.slice(0, 2), 16)
				green = parseInt(hex.slice(2, 4), 16)
				blue = parseInt(hex.slice(4, 6), 16)
			} else {
				const rgb = backgroundColor.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i)
				if (!rgb) {
					return '#FFFFFF'
				}

				red = Number(rgb[1])
				green = Number(rgb[2])
				blue = Number(rgb[3])
			}

			const getChannelLuminance = (channel) => {
				const normalizedChannel = channel / 255
				return normalizedChannel <= 0.03928
					? normalizedChannel / 12.92
					: Math.pow((normalizedChannel + 0.055) / 1.055, 2.4)
			}

			const luminance = (0.2126 * getChannelLuminance(red))
				+ (0.7152 * getChannelLuminance(green))
				+ (0.0722 * getChannelLuminance(blue))
			const contrastWithWhite = 1.05 / (luminance + 0.05)
			const contrastWithBlack = (luminance + 0.05) / 0.05

			return contrastWithWhite >= contrastWithBlack ? '#FFFFFF' : '#000000'
		},

		fetchEntregaGraphData(isBackgroundRefresh = false) {
			// En una recarga automática en segundo plano no mostramos el spinner
			// completo para no interrumpir al usuario mientras mira los gráficos.
			if (!isBackgroundRefresh) {
				this.loading = true;
			}
			if (this.$store.projectsStore.state.selectedProject) {
				const projectId = this.$store.projectsStore.state.selectedProject.id;
				this.$store.entregaStore.dispatch('getEntregaGraphsData', { project_id: projectId });
			} else if (!isBackgroundRefresh) {
				this.loading = false;
				this.showErrorMessage('No hay proyecto seleccionado');
			}
		},

		startAutoRefresh() {
			this.stopAutoRefresh();
			this.autoRefreshTimer = setInterval(() => {
				if (this.$store.projectsStore.state.selectedProject) {
					this.fetchEntregaGraphData(true);
				}
			}, this.refreshIntervalSeconds * 1000);
		},

		stopAutoRefresh() {
			if (this.autoRefreshTimer) {
				clearInterval(this.autoRefreshTimer);
				this.autoRefreshTimer = null;
			}
		},

		async exportGraphsPDF() {
			const selectedProject = this.$store.projectsStore.state.selectedProject;
			if (!selectedProject) {
				this.showErrorMessage('No hay proyecto seleccionado');
				return;
			}

			// Reservar la pestaña antes del await evita que el navegador bloquee
			// la apertura cuando la generación del PDF tarda varios segundos.
			const pdfWindow = window.open('', '_blank');
			if (!pdfWindow) {
				this.showErrorMessage('El navegador bloqueó la apertura del PDF');
				return;
			}

			this.exportingPdf = true;
			try {
				const response = await this.$store.entregaStore.dispatch('exportEntregaGraphsPDF', {
					project_id: selectedProject.id
				});
				if (response && response.code === 403) {
					pdfWindow.close();
					this.showErrorMessage(response.message || 'No tienes permiso para exportar este reporte');
					return;
				}

				if (!response || !response.pdf_url) {
					throw new Error('La respuesta no contiene una URL de PDF');
				}

				pdfWindow.location.href = response.pdf_url;
			} catch (error) {
				pdfWindow.close();
				this.showErrorMessage('No fue posible generar el PDF de Entregas');
			} finally {
				this.exportingPdf = false;
			}
		},
		
		processGraphData(data) {
			// Process status distribution data — uses real display states from the backend
			const defaultStatusColors = ['#5E85EF', '#FFC107', '#FF9800', '#4CAF50', '#8BC34A', '#009688', '#F44336', '#9E9E9E'];
			if (data.status_distribution && data.status_distribution.labels) {
				this.chartData.statusDistribution = {
					labels: data.status_distribution.labels,
					data:   data.status_distribution.data,
					colors: data.status_distribution.colors || defaultStatusColors
				};
			}
			
			// Process approval time data
			if (data.approval_time) {
				this.chartData.approvalTime = {
					labels: data.approval_time.labels,
					data: data.approval_time.data,
					colors: this.getChartColors(data.approval_time.labels.length)
				};
			}
			
			// Process approvals by job type data
			if (data.approvals_by_job_type) {
				this.chartData.approvalsByJobType = {
					labels: data.approvals_by_job_type.labels,
					approved: data.approvals_by_job_type.approved,
					rejected: data.approvals_by_job_type.rejected,
					observed: data.approvals_by_job_type.observed
				};
			}

			// Process approvals by user data
			this.chartData.approvalsByUser = data.approvals_by_user ? {
				labels: data.approvals_by_user.labels || [],
				approved: data.approvals_by_user.approved || [],
				rejected: data.approvals_by_user.rejected || [],
				observed: data.approvals_by_user.observed || []
			} : {
				labels: [],
				approved: [],
				rejected: [],
				observed: []
			};
			
			// Process monthly trend data
			if (data.monthly_trend) {
				this.chartData.monthlyTrend = {
					labels: data.monthly_trend.labels,
					data: data.monthly_trend.data
				};
			}
			
			// Process zone distribution data
			if (data.zone_distribution) {
				this.chartData.zoneDistribution = {
					labels: data.zone_distribution.labels,
					data: data.zone_distribution.data,
					colors: data.zone_distribution.colors
				};
			}
			
			// Process deliveries by specialty data
			if (data.deliveries_by_specialty) {
				this.chartData.deliveriesBySpecialty = {
					labels: data.deliveries_by_specialty.labels || [],
					approved: data.deliveries_by_specialty.approved || [],
					rejected: data.deliveries_by_specialty.rejected || [],
					observed: data.deliveries_by_specialty.observed || [],
					pending: data.deliveries_by_specialty.pending || []
				};
			}
			
			// Update stat cards
			this.statCards = {
				totalEntregas: data.total_entregas || 0,
				pendingEntregas: data.pending_entregas || 0,
				approvedEntregas: data.approved_entregas || 0,
				overdueEntregas: data.overdue_entregas || 0,
				revItoEntregas: data.rev_ito_entregas || 0,
				aprobadoAcEntregas: data.aprobado_ac_entregas || 0,
				aprobadoAcTlEntregas: data.aprobado_ac_tl_entregas || 0
			};
		},
		
		getChartColors(length) {
			return Array.from({ length }, (_, index) => {
				return this.chartColors[index % this.chartColors.length]
			});
		},
		
		showErrorMessage(customMessage = null) {
			const message = {
			message: customMessage || 'Error al cargar los datos de gráficos para entregas',
			title: 'Error',
			created: new Date(),
			type: 'error',
			icon: 'mdi-alert-circle',
			color: '#F44336'
			};
			this.$store.globalStore.dispatch('addMessage', message);
		}
	}
};
</script>

<style scoped>
.entregas-graphs {
	--ink: #1F2937;
	--ink-soft: #6B7280;
	--surface-border: #E7EAF3;
}

/* ---------- Loading ---------- */
.loading-container {
	background: white;
	border: 1px solid var(--surface-border);
	min-height: 320px;
}

/* ---------- Header ---------- */
.dashboard-header {
	display: flex;
	align-items: flex-end;
	justify-content: space-between;
	gap: 20px;
	padding: 20px 24px;
	border-radius: 14px;
	background: linear-gradient(135deg, rgba(94, 133, 239, 0.08), rgba(45, 75, 149, 0.04));
	border: 1px solid rgba(94, 133, 239, 0.16);
}

.header-eyebrow {
	display: block;
	font-size: 11px;
	font-weight: 700;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	color: #5E85EF;
	margin-bottom: 6px;
}

.header-title {
	font-size: 24px;
	font-weight: 700;
	color: var(--ink);
	margin: 0;
	line-height: 1.2;
}

.header-subtitle {
	margin: 6px 0 0;
	color: var(--ink-soft);
	font-size: 13px;
}

.export-btn {
	border-radius: 8px !important;
	text-transform: none !important;
	letter-spacing: 0 !important;
	font-weight: 600 !important;
	font-size: 13px !important;
	height: 34px !important;
	padding: 0 14px !important;
	flex-shrink: 0;
}

/* ---------- Summary Cards ---------- */
.stats-grid {
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 10px;
}

@media (min-width: 600px) {
	.stats-grid {
		grid-template-columns: repeat(3, 1fr);
	}
}

@media (min-width: 960px) {
	.stats-grid {
		grid-template-columns: repeat(4, 1fr);
		gap: 12px;
	}
}

@media (min-width: 1264px) {
	.stats-grid {
		grid-template-columns: repeat(7, 1fr);
	}
}

.stat-card {
	display: flex;
	flex-direction: column;
	align-items: center;
	text-align: center;
	gap: 6px;
	background: white;
	border-radius: 12px;
	border: 1px solid var(--surface-border);
	border-top: 3px solid var(--accent);
	padding: 14px 8px 12px;
	box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
	transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.stat-card:hover {
	transform: translateY(-3px);
	box-shadow: 0 8px 20px rgba(16, 24, 40, 0.08);
}

.stat-icon-wrap {
	flex-shrink: 0;
	width: 36px;
	height: 36px;
	border-radius: 10px;
	display: flex;
	align-items: center;
	justify-content: center;
}

.stat-body {
	min-width: 0;
	width: 100%;
}

.stat-value {
	font-size: 20px;
	font-weight: 700;
	color: var(--ink);
	line-height: 1.1;
	margin: 0;
}

.stat-label {
	margin: 3px 0 0;
	font-size: 11px;
	font-weight: 500;
	color: var(--ink-soft);
	white-space: normal;
	line-height: 1.25;
	word-break: break-word;
	min-height: 28px;
	display: flex;
	align-items: center;
	justify-content: center;
}

/* ---------- Chart Cards ---------- */
.chart-card {
	background: white;
	border: 1px solid var(--surface-border);
	border-top: 3px solid var(--chart-accent);
	border-radius: 14px;
	height: 100%;
	min-height: 360px;
	display: flex;
	flex-direction: column;
	box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
	padding: 18px 18px 12px;
}

.chart-card-header {
	display: flex;
	align-items: flex-start;
	gap: 12px;
	margin-bottom: 8px;
}

.chart-header-icon {
	flex-shrink: 0;
	width: 36px;
	height: 36px;
	border-radius: 10px;
	display: flex;
	align-items: center;
	justify-content: center;
}

.chart-header-text {
	min-width: 0;
	text-align: left;
}

.chart-title {
	font-size: 15px;
	font-weight: 700;
	color: var(--ink);
	margin: 0;
	line-height: 1.3;
}

.chart-subtitle {
	margin: 2px 0 0;
	font-size: 12px;
	color: var(--ink-soft);
	line-height: 1.3;
}

.chart-body {
	flex: 1;
	display: flex;
	flex-direction: column;
}

.empty-chart-message {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	flex: 1;
	min-height: 250px;
	color: #9E9E9E;
}

.empty-chart-message p {
	margin-top: 12px;
	font-size: 13px;
}

@media (max-width: 959px) {
	.dashboard-header {
		flex-direction: column;
		align-items: flex-start;
	}

	.export-btn {
		align-self: flex-end;
	}
}
</style>